import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Loader2, ArrowLeft, Briefcase, Eye, EyeOff, Building2, MapPin, Phone, Mail, User, Lock, Globe } from 'lucide-react';
import axios from 'axios';
import LegalModal from '../../components/LegalModal';
import { useTurnstile } from '../../components/TurnstileCaptcha';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password Input Component with eye toggle
const PasswordInput = ({ id, value, onChange, placeholder = "••••••••", ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={showPassword ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="pr-10"
        {...props}
      />
      <button
        type="button"
        onClick={() => setShowPassword(!showPassword)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
      >
        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

const RegisterLabRep = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [cguAccepted, setCguAccepted] = useState(false);
  
  // CAPTCHA
  const { token: captchaToken, isVerified, TurnstileComponent } = useTurnstile();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    country_code: 'LU',
    company_name: '',
    company_address: '',
    territory: '',
    language: language,
    role: 'medical_rep',
    gdpr_consent: false,
    marketing_consent: false
  });

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/countries`);
      setCountries(res.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (!formData.gdpr_consent) {
      toast.error('Veuillez accepter les conditions d\'utilisation');
      return;
    }

    if (!isVerified) {
      toast.error('Veuillez compléter la vérification CAPTCHA');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone,
        country_code: formData.country_code,
        language: formData.language,
        role: 'medical_rep',
        company_name: formData.company_name,
        company_address: formData.company_address,
        territory: formData.territory,
        gdpr_consent: formData.gdpr_consent,
        marketing_consent: formData.marketing_consent,
        captcha_token: captchaToken
      };

      await axios.post(`${API_URL}/api/auth/register`, payload);
      toast.success('Compte créé avec succès ! Vérifiez votre email.');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Accès aux agendas",
      description: "Consultez les créneaux dédiés aux visites médicales"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Planification optimisée",
      description: "Organisez vos tournées par zone géographique"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-pays",
      description: "Luxembourg, France, Belgique, Allemagne..."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Column - Info */}
          <div className="space-y-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">Espace Laboratoires</h1>
                  <p className="text-slate-600">Représentants médicaux & dentaires</p>
                </div>
              </div>
              <p className="text-lg text-slate-600 leading-relaxed">
                Rejoignez drbooking et accédez aux agendas des professionnels de santé 
                pour planifier efficacement vos visites commerciales.
              </p>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900">Vos avantages</h3>
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600">
                    {benefit.icon}
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">{benefit.title}</h4>
                    <p className="text-sm text-slate-600">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>🔒 Données sécurisées</strong> - Vos informations sont protégées 
                conformément au RGPD et hébergées en Europe.
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <Card className="shadow-xl border-0">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Inscription Représentant
              </CardTitle>
              <CardDescription className="text-blue-100">
                Créez votre compte professionnel en quelques minutes
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* Personal Info Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <User className="w-4 h-4" /> Informations personnelles
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="first_name">Prénom *</Label>
                      <Input
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        required
                        placeholder="Jean"
                        data-testid="rep-first-name"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="last_name">Nom *</Label>
                      <Input
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        required
                        placeholder="Dupont"
                        data-testid="rep-last-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" /> Email professionnel *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="jean.dupont@laboratoire.com"
                      data-testid="rep-email"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="phone" className="flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Téléphone *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        placeholder="+352 621 123 456"
                        data-testid="rep-phone"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="country">Pays *</Label>
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger data-testid="rep-country">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Company Info Section */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Informations entreprise
                  </h4>

                  <div className="space-y-1">
                    <Label htmlFor="company_name">Laboratoire / Entreprise *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      placeholder="Ex: Pfizer, Sanofi, Henry Schein..."
                      data-testid="rep-company"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="company_address">Adresse du laboratoire</Label>
                    <Input
                      id="company_address"
                      value={formData.company_address}
                      onChange={(e) => setFormData({ ...formData, company_address: e.target.value })}
                      placeholder="Adresse du siège ou de l'agence"
                      data-testid="rep-address"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="territory" className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" /> Région couverte *
                    </Label>
                    <Input
                      id="territory"
                      value={formData.territory}
                      onChange={(e) => setFormData({ ...formData, territory: e.target.value })}
                      required
                      placeholder="Ex: Luxembourg, Belgique, Grand Est..."
                      data-testid="rep-territory"
                    />
                  </div>
                </div>

                {/* Password Section */}
                <div className="space-y-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Sécurité
                  </h4>

                  <div className="space-y-1">
                    <Label htmlFor="password">Mot de passe *</Label>
                    <PasswordInput
                      id="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Minimum 8 caractères"
                      data-testid="rep-password"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                    <PasswordInput
                      id="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required
                      minLength={8}
                      placeholder="Retapez le mot de passe"
                      data-testid="rep-confirm-password"
                    />
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-red-500 text-sm">Les mots de passe ne correspondent pas</p>
                    )}
                  </div>
                </div>

                {/* GDPR & CGU Consent */}
                <div className="space-y-3 p-4 bg-slate-50 border rounded-lg">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="gdpr_consent"
                      checked={formData.gdpr_consent}
                      onChange={(e) => setFormData({ ...formData, gdpr_consent: e.target.checked })}
                      required
                      className="mt-1"
                      data-testid="rep-gdpr-consent"
                    />
                    <label htmlFor="gdpr_consent" className="text-sm text-slate-700">
                      J'accepte les{' '}
                      <button 
                        type="button" 
                        onClick={() => setShowLegalModal(true)}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        Conditions Générales d'Utilisation
                      </button>
                      {' '}et la{' '}
                      <Link to="/privacy-policy" className="text-blue-600 hover:underline font-medium">
                        Politique de Confidentialité
                      </Link>
                      {' '}*
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="marketing_consent"
                      checked={formData.marketing_consent}
                      onChange={(e) => setFormData({ ...formData, marketing_consent: e.target.checked })}
                      className="mt-1"
                      data-testid="rep-marketing-consent"
                    />
                    <label htmlFor="marketing_consent" className="text-sm text-slate-600">
                      J'accepte de recevoir des informations sur les nouveaux services et fonctionnalités
                    </label>
                  </div>
                </div>

                {/* CAPTCHA */}
                <div className="flex justify-center">
                  <TurnstileComponent />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full h-12 text-lg bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                  disabled={loading || !isVerified}
                  data-testid="rep-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Création en cours...
                    </>
                  ) : (
                    <>
                      <Briefcase className="w-5 h-5 mr-2" />
                      Créer mon compte
                    </>
                  )}
                </Button>

                {/* Login Link */}
                <p className="text-center text-sm text-slate-600">
                  Déjà inscrit ?{' '}
                  <Link to="/login" className="text-blue-600 hover:underline font-medium">
                    Se connecter
                  </Link>
                </p>

                {/* Other Registration Options */}
                <div className="pt-4 border-t text-center">
                  <p className="text-sm text-slate-500 mb-3">Autres types d'inscription :</p>
                  <div className="flex justify-center gap-3">
                    <Link to="/register?type=patient">
                      <Button variant="outline" size="sm" className="gap-2">
                        <User className="w-4 h-4" />
                        Patient
                      </Button>
                    </Link>
                    <Link to="/register?type=professional">
                      <Button variant="outline" size="sm" className="gap-2">
                        <Calendar className="w-4 h-4" />
                        Professionnel
                      </Button>
                    </Link>
                  </div>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal 
        isOpen={showLegalModal} 
        onClose={() => setShowLegalModal(false)}
        onAccept={() => {
          setCguAccepted(true);
          setFormData({ ...formData, gdpr_consent: true });
          setShowLegalModal(false);
        }}
      />
    </div>
  );
};

export default RegisterLabRep;
